import React, { useEffect, useState, useCallback } from "react";
import { api, formatCPF } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent } from "@/components/ui/card";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Search, Users } from "lucide-react";

const emptyForm = { nome: "", cpf: "", telefone: "", observacoes: "" };

export default function Drivers() {
    const [drivers, setDrivers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [open, setOpen] = useState(false);
    const [form, setForm] = useState(emptyForm);
    const [editingId, setEditingId] = useState(null);
    const [filter, setFilter] = useState("");
    const [confirmDelete, setConfirmDelete] = useState(null);

    const load = useCallback(async () => {
        setLoading(true);
        const res = await api.get("/drivers");
        setDrivers(res.data);
        setLoading(false);
    }, []);

    useEffect(() => { load(); }, [load]);

    const openCreate = () => {
        setForm(emptyForm);
        setEditingId(null);
        setOpen(true);
    };

    const openEdit = (d) => {
        setForm({ nome: d.nome, cpf: d.cpf, telefone: d.telefone || "", observacoes: d.observacoes || "" });
        setEditingId(d.id);
        setOpen(true);
    };

    const save = async () => {
        if (!form.nome.trim() || !form.cpf.trim()) {
            toast.error("Nome e CPF são obrigatórios");
            return;
        }
        try {
            if (editingId) {
                await api.put(`/drivers/${editingId}`, form);
                toast.success("Motorista atualizado!");
            } else {
                await api.post("/drivers", form);
                toast.success("Motorista cadastrado!");
            }
            setOpen(false);
            load();
        } catch (e) {
            toast.error("Erro ao salvar");
        }
    };

    const del = async (id) => {
        try {
            await api.delete(`/drivers/${id}`);
            toast.success("Motorista removido");
            load();
        } catch (e) {
            toast.error("Erro ao remover");
        } finally {
            setConfirmDelete(null);
        }
    };

    const filtered = drivers.filter((d) =>
        (d.nome + d.cpf + (d.telefone || "")).toLowerCase().includes(filter.toLowerCase())
    );

    return (
        <div className="space-y-6" data-testid="drivers-page">
            <div className="flex items-center justify-between gap-4 flex-wrap">
                <div>
                    <h2 className="text-3xl font-extrabold text-foreground">Motoristas</h2>
                    <p className="text-sm text-muted-foreground mt-1">Agentes responsáveis pelos veículos</p>
                </div>
                <Button onClick={openCreate} data-testid="btn-new-driver" className="gap-2">
                    <Plus className="w-4 h-4" /> Novo motorista
                </Button>
            </div>

            <Card>
                <CardContent className="p-6">
                    <div className="relative max-w-md mb-4">
                        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                        <Input
                            placeholder="Buscar por nome, CPF ou telefone..."
                            className="pl-10"
                            value={filter}
                            onChange={(e) => setFilter(e.target.value)}
                            data-testid="input-search-driver"
                        />
                    </div>

                    {loading ? (
                        <div className="text-muted-foreground py-8 text-center">Carregando...</div>
                    ) : filtered.length === 0 ? (
                        <div className="py-14 text-center border-2 border-dashed border-border rounded-xl">
                            <Users className="w-10 h-10 mx-auto text-muted-foreground" />
                            <div className="mt-3 font-semibold">Nenhum motorista cadastrado</div>
                            <div className="text-sm text-muted-foreground mt-1">Clique em "Novo motorista" para começar</div>
                        </div>
                    ) : (
                        <div className="overflow-x-auto">
                            <table className="w-full text-sm">
                                <thead>
                                    <tr className="text-left text-muted-foreground border-b border-border">
                                        <th className="py-3 px-3 font-semibold">Nome</th>
                                        <th className="py-3 px-3 font-semibold">CPF</th>
                                        <th className="py-3 px-3 font-semibold">Telefone</th>
                                        <th className="py-3 px-3 font-semibold">Observações</th>
                                        <th className="py-3 px-3 font-semibold text-right">Ações</th>
                                    </tr>
                                </thead>
                                <tbody data-testid="drivers-table-body">
                                    {filtered.map((d) => (
                                        <tr key={d.id} className="border-b border-border row-hover">
                                            <td className="py-3 px-3 font-medium">{d.nome}</td>
                                            <td className="py-3 px-3 font-mono text-xs">{formatCPF(d.cpf)}</td>
                                            <td className="py-3 px-3">{d.telefone || "-"}</td>
                                            <td className="py-3 px-3 text-muted-foreground">{d.observacoes || "-"}</td>
                                            <td className="py-3 px-3 text-right">
                                                <div className="flex gap-2 justify-end">
                                                    <Button variant="ghost" size="sm" onClick={() => openEdit(d)} data-testid={`btn-edit-driver-${d.id}`}>
                                                        <Pencil className="w-4 h-4" />
                                                    </Button>
                                                    <Button variant="ghost" size="sm" onClick={() => setConfirmDelete(d)} data-testid={`btn-delete-driver-${d.id}`}>
                                                        <Trash2 className="w-4 h-4 text-destructive" />
                                                    </Button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </CardContent>
            </Card>

            <Dialog open={open} onOpenChange={setOpen}>
                <DialogContent data-testid="driver-dialog">
                    <DialogHeader>
                        <DialogTitle>{editingId ? "Editar motorista" : "Novo motorista"}</DialogTitle>
                        <DialogDescription>Cadastre o agente responsável pelo veículo</DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4 py-2">
                        <div>
                            <Label>Nome completo *</Label>
                            <Input value={form.nome} onChange={(e) => setForm({ ...form, nome: e.target.value })} data-testid="input-driver-nome" />
                        </div>
                        <div>
                            <Label>CPF *</Label>
                            <Input value={form.cpf} onChange={(e) => setForm({ ...form, cpf: e.target.value })} placeholder="000.000.000-00" data-testid="input-driver-cpf" />
                        </div>
                        <div>
                            <Label>Telefone</Label>
                            <Input value={form.telefone} onChange={(e) => setForm({ ...form, telefone: e.target.value })} data-testid="input-driver-telefone" />
                        </div>
                        <div>
                            <Label>Observações</Label>
                            <Textarea value={form.observacoes} onChange={(e) => setForm({ ...form, observacoes: e.target.value })} data-testid="input-driver-obs" />
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setOpen(false)} data-testid="btn-cancel-driver">Cancelar</Button>
                        <Button onClick={save} data-testid="btn-save-driver">Salvar</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            <AlertDialog open={!!confirmDelete} onOpenChange={(v) => !v && setConfirmDelete(null)}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>Remover motorista?</AlertDialogTitle>
                        <AlertDialogDescription>
                            Esta ação não pode ser desfeita. O motorista <strong>{confirmDelete?.nome}</strong> será removido.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel data-testid="btn-cancel-delete">Cancelar</AlertDialogCancel>
                        <AlertDialogAction onClick={() => del(confirmDelete.id)} data-testid="btn-confirm-delete-driver">Remover</AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    );
}
